import React, { useEffect, useRef, useState } from 'react';
import { GdkConsolidatedSummary, IConsolidatedSummary } from '@gdk/consolidated-summary';
import { AddAgentNoteRequest, AGENT_NOTES_RESPONSE, AgentNotesData, AgentNotesRequest } from "../api/types/agent-notes-modal";
import { usePageLoadingContext } from "../contexts/PageLoadingContext";
import { useApi } from "../api";
import { API_ERROR_MESSAGE } from "../api/launchpad-api";
import { useAuthContext } from "../contexts/AuthContextProvider";
import { useConsolidatedSummary } from '../contexts/ConsolidatedSummaryContext';
import { AgentConsolidatedQuoteInfo } from './agent-upsert-quote-info';
import { useSelector } from "react-redux";
import store, { RootState } from "../store";
import { PARTNER_DETAILS_RESPONSE } from '../api/types/partner-details';

interface ConsolidatedSummaryComponentProps {
    onResetForm: () => void;
}

const ConsolidatedSummaryComponent: React.FC<ConsolidatedSummaryComponentProps> = ({ onResetForm }) => {
    const { quoteInfos } = useConsolidatedSummary();
    const consolidatedSummaryRef = useRef<GdkConsolidatedSummary | null>(null);
    const expandMoreRef = useRef<HTMLAnchorElement>(null); // Create a reference for the icon
    const [selectedId, setSelectedId] = useState<string>(quoteInfos[0]?.id || '');
    const { showLoader, hideLoader } = usePageLoadingContext();
    const [showAlert, setShowAlert] = useState(false);
    const api = useApi();
    const [agentNotes, setQuoteNotes] = useState<AgentNotesData[]>([]);
    const authContext = useAuthContext();
    const [alertMessage, setAlertMessage] = useState({ title: "", message: "" });
    const [agentNoteTextArea, setAgentNoteTextArea] = useState<string>("");
    const [isExpanded, setIsExpanded] = useState(false);
    const [isExpandedMore, setIsExpandedMore] = useState(false);
    const prefillHandlerRef = useRef<() => void>();     // Ref to store the prefill handler from AgentConsolidatedQuoteInfo
    const [isPlogOpen, setIsPlogOpen] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState<string>("");
    const quote = useSelector((state: RootState) => state.quote.quote);

    useEffect(() => {
        const options: IConsolidatedSummary = {
            content: '#consolidated-summary-component',
            initiallyShown: false,
            initiallyExpanded: false,
            loadPlog: () => {
                return new Promise<void>((resolve) => {
                    resolve();
                });
            }
        };

        const element = document.querySelector(options.content as string);
        if (element) {
            consolidatedSummaryRef.current = new GdkConsolidatedSummary(options);
            handleShow();
        } else {
            console.error('Element not found:', options.content);
        }

        // Cleanup on unmount
        return () => {
            consolidatedSummaryRef.current?.destroy();
        };
    }, []);

    useEffect(() => {
        if (selectedId) {
            getAgentNotes(selectedId, quoteInfos.find(q => q.id === selectedId)?.referenceNumber);
            if (!selectedQuote?.redirectUrl) {
                api.commercialAgentApi.getPartnerData()
                    .then(response => {
                        if (response.type === PARTNER_DETAILS_RESPONSE) {
                            const partner = response.data.find(
                                (carrier: { name: string }) =>
                                    carrier.name.toLowerCase() === (selectedQuote?.source || '').toLowerCase()
                            );
                            if (partner) {
                                setRedirectUrl(partner.redirectUrl);
                            } else {
                                setRedirectUrl("");
                            }
                        } else {
                            console.error("Error fetching partner data:", response);
                        }
                    })
                    .catch(error => {
                        console.error("Error fetching partner data:", error);
                    });
            } else {
                setRedirectUrl(selectedQuote?.redirectUrl || "");
            }
        }
    }, [selectedId]);

    useEffect(() => {
        if (quoteInfos.length > 0) {
            setSelectedId(quoteInfos[0].id); // Set the default selected quote
            handleExpand();
        }
    }, [quoteInfos]);

    useEffect(() => {
        if (quote.data.CarrierDetail && quote.data.CarrierDetail.length > 0) {
            expandMoreRef.current?.click();
        }
    }, [quote.data.CarrierDetail]);

    const handleShow = () => {
        consolidatedSummaryRef.current?.show();
    };

    const handleExpand = () => {
        setIsExpanded(true);
        consolidatedSummaryRef.current?.expand();
    }

    const handleRetract = () => {
        setIsExpanded(false);
        setIsExpandedMore(false);
        consolidatedSummaryRef.current?.retract();
    }

    const handleToggle = () => {
        if (isExpanded && isExpandedMore) {
            handleExpand();
        } else if (isExpanded && !isExpandedMore) {
            handleRetract();
        } else if (!isExpanded && !isExpandedMore) {
            handleExpand();
        } else if (isExpanded) {
            handleExpand();
        } else {
            handleRetract();
        }
    }

    const handleIdChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = event.target.value;

        const [source, referenceNumber, lineOfBusinessStr] = selectedValue.split('|');
        const lineOfBusiness = lineOfBusinessStr ? lineOfBusinessStr.split(',') : [];

        const newSelectedQuote = quoteInfos.find(quote =>
            quote.source === source &&
            quote.referenceNumber === referenceNumber &&
            JSON.stringify(quote.lineOfBusiness.sort()) === JSON.stringify(lineOfBusiness.sort())
        );

        if (newSelectedQuote) {
            const currentSelectedQuote = quoteInfos.find(quote => quote.id === selectedId);

            const hasSourceChanged = currentSelectedQuote?.source !== newSelectedQuote.source;
            const hasReferenceNumberChanged = currentSelectedQuote?.referenceNumber !== newSelectedQuote.referenceNumber;
            const hasLineOfBusinessChanged = JSON.stringify(currentSelectedQuote?.lineOfBusiness?.sort()) !== JSON.stringify(newSelectedQuote.lineOfBusiness?.sort());

            if (hasSourceChanged || hasReferenceNumberChanged || hasLineOfBusinessChanged) {
                setSelectedId(newSelectedQuote.id);
            }
        }
    };

    function convertUTCDateToLocalDate(date: string) {
        var newDate = new Date(date);
        newDate.setMinutes(newDate.getMinutes() - newDate.getTimezoneOffset());
        return newDate.toLocaleString();
    }

    function getAgentNotes(quoteInfoId: string, quoteReferenceNumber?: string) {
        let request: AgentNotesRequest = { quoteInfoId: quoteInfoId, quoteReferenceNumber: quoteReferenceNumber };
        if (quoteInfoId) {
            showLoader();
            api.commercialAgentApi.getAgentNotes(request, authContext.getAuthToken()).then((response) => {
                if (response.type !== AGENT_NOTES_RESPONSE || response.data.partnerQuoteNotes.length === 0) {
                    setQuoteNotes([]);
                    hideLoader();
                    return;
                }
                let quoteNotes = response.data.partnerQuoteNotes as AgentNotesData[];
                setQuoteNotes(quoteNotes);
                hideLoader();
            }).catch((error) => {
                setShowAlert(true);
                setAlertMessage({
                    title: "Thank you for your request.",
                    message: API_ERROR_MESSAGE
                });
                hideLoader();
            });
        }
    };

    async function submitForm(): Promise<void> {
        const userName = authContext.getUserName();
        const agentId = authContext.getEmployeeId();
        let request: AddAgentNoteRequest = {
            quoteInfoId: selectedId,
            note: agentNoteTextArea,
            agentId: agentId,
            agentName: userName?.toString() || "",
            callReason: "1",
            createdDate: new Date().toISOString(),
            quoteReferenceNumber: selectedQuote?.referenceNumber || ""
        };
        if (selectedId && agentNoteTextArea) {
            await api.commercialAgentApi.addAgentNote(request, authContext.getAuthToken()).then((response) => {
                if (response.type !== AGENT_NOTES_RESPONSE) {
                    return;
                }
                getAgentNotes(selectedId, selectedQuote?.referenceNumber);
                setAgentNoteTextArea("");
                const inputContent = document.querySelector('.input-content');
                if (inputContent) {
                    inputContent.textContent = "";
                }
                return;
            }).catch((error) => {
                setShowAlert(true);
                setAlertMessage({
                    title: "Error",
                    message: API_ERROR_MESSAGE
                });
            });
        }
    }


    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            submitForm();
        }
    };

    const formatPhoneNumber = (phoneNumber: string) => {
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`;
        }
        return phoneNumber;
    };

    const handleExpandMore = () => {
        if (!isPlogOpen)
            setIsExpandedMore(true);
    };

    const handleisPlogOpen = () => {
        setIsPlogOpen(!isPlogOpen);
        handleExpandLess();
    }

    const handleExpandLess = () => {
        setIsExpanded(true);
        setIsExpandedMore(false);
    };

    const selectedQuote = quoteInfos.find(quote => quote.id === selectedId);

    const handlePrefillFinalOutcome = () => {
        setIsExpandedMore(true);
        if (prefillHandlerRef.current) {
            prefillHandlerRef.current();
        }
        expandMoreRef.current?.click();
    };

    return (
        <div>
            <div
                id="consolidated-summary-component"
                style={{ width: isExpandedMore ? '500px' : '' }}
            >
                <div
                    className="consolidated-summary-wrapper"
                    style={{ width: isExpandedMore ? 'auto' : '' }}
                >
                    <div
                        className="consolidated-summary-header"
                        style={{ width: isExpandedMore ? 'auto' : '' }}
                    >
                        <div className="consolidated-icon-frament"
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '4rem'
                            }}>
                            {!isExpandedMore && (<span
                                className="geico-icon icon-resize geico-icon--xsmall"
                                onClick={() => { handleToggle() }}
                            ></span>)}
                        </div>
                        <div className="consolidated-header-wrapper">
                            <div className="consolidated-summary-driver-info">
                                <div className="form-field">
                                    <div className="select-box">
                                        <select
                                            id="consolidate-summary-auto-select-box"
                                            name="consolidate-summary-auto-select-box"
                                            onChange={handleIdChange}
                                            value={selectedQuote ? `${selectedQuote.source}|${selectedQuote.referenceNumber}|${selectedQuote.lineOfBusiness.join(',')}` : ''}
                                        >
                                            {quoteInfos.map((quote, index) => (
                                                <option
                                                    key={`${quote.source}-${quote.referenceNumber}-${quote.lineOfBusiness.join('-')}`}
                                                    value={`${quote.source}|${quote.referenceNumber}|${quote.lineOfBusiness.join(',')}`}
                                                >
                                                    {quote.referenceNumber} ({quote.source})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {selectedQuote && (
                                    <div>
                                        <button 
                                            type="button" 
                                            className="link-button" 
                                            onClick={(e) => { 
                                                e.preventDefault(); 
                                                handlePrefillFinalOutcome(); 
                                            }}
                                        >
                                            Edit Entry
                                        </button>
                                        <h4>{selectedQuote.firstName} {selectedQuote.lastName}</h4>
                                        <div>{selectedQuote.email}</div>
                                        <div>{formatPhoneNumber(selectedQuote.phoneNumber)}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="consolidated-summary-content">
                        <div className="consolidated-summary-content-tabs vertical-tabs">
                            <div className="vertical-tabs-controls" style={{ width: isExpandedMore ? '10%' : '' }}>
                                <a className="tab" onClick={handleExpandLess}><span className="icon icon-account-info"></span></a>
                                <a className="tab" onClick={handleExpandMore} ref={expandMoreRef}><span className="icon icon-b2b"></span></a> {/* Attach the reference */}
                            </div>
                            <div className="vertical-tabs-panels">
                                <div className="panel">
                                    <div className="panel-content">
                                        <h3>Account Info</h3>
                                        {selectedQuote && (
                                            <div className="quote-details">
                                                <div><strong>State Code:</strong></div>
                                                <div>{selectedQuote.stateCode}</div>
                                                <hr />
                                                <div><strong>Line of Business:</strong></div>
                                                <div>{selectedQuote.lineOfBusiness.join(", ")}</div>
                                                <hr />
                                                <div><strong>Date of Birth:</strong></div>
                                                <div>{selectedQuote.dateOfBirth}</div>
                                                <hr />
                                                <div><strong>Zip Code:</strong></div>
                                                <div>{selectedQuote.zipCode}</div>
                                                <hr />
                                                <div><strong>Source:</strong></div>
                                                <div>
                                                    {redirectUrl ? (
                                                        <a href={redirectUrl} target="_blank" rel="noopener noreferrer">
                                                            {selectedQuote.source}
                                                        </a>
                                                    ) : (
                                                        selectedQuote.source
                                                    )}
                                                </div>
                                                <hr />
                                                <div><strong>Premium:</strong></div>
                                                <div>{selectedQuote.premium}</div>
                                                <hr />
                                                <div><strong>Quote Last Modify Date:</strong></div>
                                                <div>{new Date(selectedQuote.quoteUpdatedDate).toLocaleDateString()}</div>
                                                <hr />
                                                <div><strong>Most Recent IA/Customer Session login date/time:</strong></div>
                                                <div>{convertUTCDateToLocalDate(selectedQuote.quoteUpdatedDate)}</div>
                                                <hr />
                                                <div><strong>First Name:</strong></div>
                                                <div>{selectedQuote.firstName}</div>
                                                <hr />
                                                <div><strong>Business Name:</strong></div>
                                                <div>{selectedQuote.businessName}</div>
                                                <hr />
                                                <div><strong>Business Address:</strong></div>
                                                <div>{selectedQuote.businessAddress}</div>
                                                <hr />
                                                <div><strong>Business Class Description:</strong></div>
                                                <div>{selectedQuote.businessClassDescription}</div>
                                                <hr />
                                                {selectedQuote.quoteOutcome?.quoteOutcomeType && (
                                                    <>
                                                        <div><strong>Quote Outcome:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.quoteOutcomeType}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.primaryOutcomeType && (
                                                    <>
                                                        <div><strong>Type Of Quote:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.primaryOutcomeType}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.secondaryOutcomeType && (
                                                    <>
                                                        <div><strong>Subtype:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.secondaryOutcomeType}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.policyNumber && (
                                                    <>
                                                        <div><strong>Policy Number:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.policyNumber}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.effectiveDate && (
                                                    <>
                                                        <div><strong>Effective Date:</strong></div>
                                                        <div>{new Date(selectedQuote.quoteOutcome?.effectiveDate).toLocaleDateString()}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.expirationDate && (
                                                    <>
                                                        <div><strong>Expiration Date:</strong></div>
                                                        <div>{new Date(selectedQuote.quoteOutcome?.expirationDate).toLocaleDateString()}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.salesDate && (
                                                    <>
                                                        <div><strong>Sales Date:</strong></div>
                                                        <div>{new Date(selectedQuote.quoteOutcome?.salesDate).toLocaleDateString()}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.leadSource && (
                                                    <>
                                                        <div><strong>Lead Source:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.leadSource}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.language && (
                                                    <>
                                                        <div><strong>Language:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.language}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.paymentPlan && (
                                                    <>
                                                        <div><strong>Payment Plan:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.paymentPlan}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.paymentConfirmationNumber && (
                                                    <>
                                                        <div><strong>Payment Confirmation Number:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.paymentConfirmationNumber}</div>
                                                        <hr />
                                                    </>
                                                )}
                                                {selectedQuote.quoteOutcome?.isEndorsement && (
                                                    <>
                                                        <div><strong>Is Endorsement:</strong></div>
                                                        <div>{selectedQuote.quoteOutcome?.isEndorsement ? "Yes" : "No"}</div>
                                                        <hr />
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="panel"
                                    style={{ width: isExpandedMore ? '90%' : '' }}
                                >
                                    <div className="panel-content" >
                                        <AgentConsolidatedQuoteInfo
                                            isExpandedMore={isExpandedMore}
                                            prefillData={selectedQuote}
                                            setActivatePrefillHandler={handler => { prefillHandlerRef.current = handler; }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="consolidated-plog-items">
                        <div className="plog-icon" onClick={handleisPlogOpen}>
                            <span className="geico-icon icon-plog geico-icon--actionable"></span>
                        </div>
                    </div>
                    <div className="plog">
                        <div className="plog-title-bar">Policy Notes | {selectedQuote?.referenceNumber || selectedId}
                            <div className="window-controls">
                                <span className="plogResizeBtn geico-icon icon-resize geico-icon--xsmall"></span>
                                <span className="plogMinimizeBtn geico-icon icon-collapse geico-icon--xsmall" onClick={handleisPlogOpen}></span>
                            </div>
                        </div>
                        <div className="plog-content-wrapper policy-notes-list-view">
                            <div className="plog-content" style={{ flex: '0 1 90%' }}>
                                {agentNotes.length > 0 ? (
                                    agentNotes.map((note, index) => (
                                        <p key={index} className="policy-note">
                                            <span className="note-details">
                                                <span className="note-details-container">
                                                    <span className="note-details-container">
                                                        <span className="date">{new Date(convertUTCDateToLocalDate(note.createdDate)).toLocaleDateString()}</span>
                                                        <span className="time"> at {new Date(convertUTCDateToLocalDate(note.createdDate)).toLocaleTimeString()}</span>
                                                        <br />
                                                        <span className="userId">by {note.agentName}</span>
                                                    </span>
                                                </span>
                                                <span className="delete-note icon-close hidden modal-trigger-pnote"></span>
                                            </span>
                                            {note.note}
                                        </p>
                                    ))
                                ) : (
                                    <p className="policy-note">No notes available.</p>
                                )}
                            </div>
                            <div className="plog-input-bar" style={{ flex: '0 1 10%' }}>
                                <form className="policy-note-input-form" action="#" method="post">
                                    <div className="form-field">
                                        <div className="input-field">
                                            <div className="input-content" contentEditable="true" onInput={(e) => setAgentNoteTextArea(e.currentTarget.textContent || "")} onKeyPress={handleKeyPress}></div>
                                        </div>
                                        <span className="send-btn icon icon-send" onClick={submitForm}></span>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div >
    );
};

export default ConsolidatedSummaryComponent;